import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Cpu, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { errMsg } from "@/lib/api";

const DEMO = [["Super Admin", "admin@gamerznation.com", "Admin@123"], ["Sales", "sales@gamerznation.com", "Gamerz@123"],
  ["Technician", "technician@gamerznation.com", "Gamerz@123"], ["Support", "support@gamerznation.com", "Gamerz@123"]];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/dashboard" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col justify-between p-12 bg-zinc-950 text-zinc-100 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1696710257827-75e2e5954059?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-white text-black flex items-center justify-center"><Cpu className="h-4 w-4" /></div>
          <span className="font-display font-bold text-lg">Gamerz Nation</span>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl font-bold leading-tight">From first inquiry to final handover.</h1>
          <p className="mt-4 text-zinc-300 text-sm leading-relaxed">Leads, quotations, builds, inventory and after-sales support for custom PC manufacturing — in one workspace.</p>
        </div>
        <div className="relative text-xs text-zinc-400">© 2026 Gamerz Nation Pvt. Ltd.</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div>
            <h2 className="text-2xl font-bold">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your team credentials to access the CRM.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          </div>
          {err && <div className="text-sm text-destructive" data-testid="login-error">{err}</div>}
          <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Sign in
          </Button>
          <div className="rounded-lg border p-4 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map(([r, e, p]) => (
                <button type="button" key={e} onClick={() => { setEmail(e); setPassword(p); }} data-testid={`demo-login-${r.toLowerCase().replace(" ", "-")}`}
                  className="text-left text-xs rounded-md border px-2.5 py-2 hover:border-primary hover:bg-secondary/50 transition-colors">{r}</button>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
